import { apiFetch, tokens } from "./client.ts";

export type JobStatus = "new" | "scheduled" | "en_route" | "on_site" | "completed" | "cancelled";
export type JobPriority = "low" | "normal" | "high" | "urgent";
export type JobEventType =
  | "status_change"
  | "note"
  | "photo"
  | "location_ping"
  | "eta_update"
  | "signature"
  | "assignment";

export interface Job {
  id: string;
  account_id: string;
  site_id: string;
  lead_id: string | null;
  client_id: string;
  technician_id: string | null;
  service_catalog_item_id: string | null;
  created_by_user_id: string | null;
  job_number: string;
  title: string;
  description: string | null;
  status: JobStatus;
  priority: JobPriority;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  address_lat: string | null;
  address_lng: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  duration_estimate_minutes: number;
  price_estimate: string | null;
  internal_notes: string | null;
  asap: boolean;
  client_name: string | null;
  client_phone: string | null;
  lead_property_type: string | null;
  lead_gate_operator_kind: string | null;
  lead_job_details: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobEvent {
  id: string;
  job_id: string;
  event_type: JobEventType;
  actor_user_id: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface JobDetail extends Job {
  events: JobEvent[];
}

export interface JobList {
  items: Job[];
  total: number;
  limit: number;
  offset: number;
}

export interface JobAddress {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  address_lat?: string | number;
  address_lng?: string | number;
}

export async function listJobs(params: {
  status?: JobStatus;
  site_id?: string;
  technician_id?: string;
  lead_id?: string;
  date_from?: string;
  date_to?: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<JobList> {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== "") usp.set(k, String(v));
  }
  return apiFetch<JobList>(`/jobs?${usp}`);
}

export async function getJob(id: string): Promise<JobDetail> {
  return apiFetch<JobDetail>(`/jobs/${id}`);
}

export async function createJob(payload: {
  site_id: string;
  client_id: string;
  title: string;
  description?: string;
  priority?: JobPriority;
  technician_id?: string;
  service_catalog_item_id?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  duration_estimate_minutes?: number;
  price_estimate?: string | number;
  internal_notes?: string;
} & JobAddress): Promise<Job> {
  return apiFetch<Job>("/jobs", { method: "POST", body: JSON.stringify(payload) });
}

export async function createJobFromLead(payload: {
  lead_id: string;
  client_id?: string;
  title?: string;
  description?: string;
  priority?: JobPriority;
  technician_id?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  duration_estimate_minutes?: number;
  price_estimate?: string | number;
} & JobAddress): Promise<Job> {
  return apiFetch<Job>("/jobs/from-lead", { method: "POST", body: JSON.stringify(payload) });
}

export async function updateJob(id: string, patch: Partial<Job>): Promise<Job> {
  return apiFetch<Job>(`/jobs/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

export async function deleteJob(id: string): Promise<void> {
  await apiFetch<void>(`/jobs/${id}`, { method: "DELETE" });
}

export async function assignJob(id: string, technician_id: string): Promise<Job> {
  return apiFetch<Job>(`/jobs/${id}/assign`, {
    method: "POST",
    body: JSON.stringify({ technician_id }),
  });
}

export async function transitionJob(id: string, to_status: JobStatus, note?: string, notify_client?: boolean): Promise<Job> {
  return apiFetch<Job>(`/jobs/${id}/transition`, {
    method: "POST",
    body: JSON.stringify({ to_status, note, notify_client: notify_client ?? true }),
  });
}

export async function addJobNote(id: string, note: string): Promise<JobEvent> {
  return apiFetch<JobEvent>(`/jobs/${id}/notes`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });
}

export interface MagicLinkResponse {
  job_id: string;
  token: string;
  url: string;
  expires_at: string;
}

export async function mintJobMagicLink(id: string, ttl_days?: number): Promise<MagicLinkResponse> {
  return apiFetch<MagicLinkResponse>(`/jobs/${id}/magic-link`, {
    method: "POST",
    body: JSON.stringify({ ttl_days }),
  });
}

export async function revokeJobMagicLink(id: string): Promise<void> {
  await apiFetch<void>(`/jobs/${id}/magic-link`, { method: "DELETE" });
}

export type TestEvent = "scheduled" | "en_route" | "on_site" | "completed";

export interface TestSendResult {
  event: TestEvent;
  results: Array<{ channel: "sms" | "email"; status: string; to: string; error: string | null }>;
}

export async function sendTestNotification(id: string, event: TestEvent): Promise<TestSendResult> {
  return apiFetch<TestSendResult>(`/jobs/${id}/send-test`, {
    method: "POST",
    body: JSON.stringify({ event }),
  });
}

export interface NotifyTechResult {
  results: Array<{ channel: "sms" | "email"; status: string; to: string; error: string | null }>;
}

export async function notifyTech(id: string): Promise<NotifyTechResult> {
  return apiFetch<NotifyTechResult>(`/jobs/${id}/notify-tech`, { method: "POST" });
}

// ── Attachments (photos from the online booking widget) ──────────────────────

export interface JobAttachment {
  id: string;
  original_filename: string | null;
  mime: string;
  size_bytes: number;
  url: string; // backend-proxied path; fetch with Bearer then blob-URL it
  created_at: string;
}

export async function listJobAttachments(id: string): Promise<{ items: JobAttachment[] }> {
  return apiFetch<{ items: JobAttachment[] }>(`/jobs/${id}/attachments`);
}

/**
 * Fetch an attachment through the backend with auth and return an object URL.
 * Caller must URL.revokeObjectURL when unmounting (same pattern as recordings).
 */
export async function fetchJobAttachmentBlobUrl(jobId: string, attachmentId: string): Promise<string> {
  const resp = await fetch(`/api/v1/jobs/${jobId}/attachments/${attachmentId}/file`, {
    headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
  });
  if (!resp.ok) {
    throw new Error(`attachment fetch failed (${resp.status})`);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
